import React, { useState } from 'react';
import PropTypes from 'prop-types';
import ExpandablePanel from 'components/expandable-panel';
import Ingredient from './ingredient';
import fullCopy from 'js/fullCopy.js';

const Ingredients = ({
  lang = {
    increaseNumberOfServingsAriaLabel: 'Høyere antall porsjoner',
    decreaseNumberOfServingsAriaLabel: 'Lavere antall porsjoner',
  },
  heading,
  sections,
  servingsDefaultNumber,
}) => {
  const servingNumber = servingsDefaultNumber || 1;
  const [currentSections, setCurrentSections] = useState([]);

  const [currentServings, setCurrentServings] = useState(servingNumber);

  const handleKeyDown = e => {
    if (e.key === '-' || e.key === '.') {
      e.preventDefault();
    }
  };

  const handlePaste = e => {
    e.preventDefault();
    const pastedText = (e.clipboardData || window.clipboardData).getData(
      'text'
    );
    const sanitizedText = pastedText.replace(/[-.]/g, '');
    const updatedText =
      e.target.value.slice(0, e.target.selectionStart) +
      sanitizedText +
      e.target.value.slice(e.target.selectionEnd);
    setCurrentServings(parseInt(updatedText));
  };

  const handleServingsInputChange = v => {
    setCurrentServings(parseInt(v));
  };

  const increaseServings = () => {
    setCurrentServings(currentServings + 1);
  };

  const decreaseServings = () => {
    setCurrentServings(
      currentServings > 1 ? currentServings - 1 : currentServings
    );
  };

  const calcAmount = (amount, ratio) => {
    const str = (amount * ratio).toFixed(2);
    const endingZeroes = /(\.0*$)/;
    const tmpSrt = str.replace(endingZeroes, '');
    return parseFloat(tmpSrt);
  };

  const updateSections = () => {
    const newSections = fullCopy(sections);
    const servings = currentServings > 1 ? currentServings : 1;
    const ratio = servings / servingNumber;

    newSections.forEach(section => {
      section.ingredients.forEach(ingredient => {
        ingredient.amount = calcAmount(ingredient.amount, ratio);
      });
    });

    setCurrentSections(newSections);
  };

  React.useEffect(() => {
    updateSections();
  }, []);

  React.useEffect(() => {
    updateSections();
  }, [currentServings]);

  return (
    <ExpandablePanel
      toggleTextShow={lang.mobileShowLabel}
      toggleTextHide={lang.mobileHideLabel}
      toggleElement="h2"
    >
      <section className="ingredients">
        {heading && <h2 className="ingredients__heading">{heading}</h2>}
        <div className="ingredients__servings">
          <label
            className="ingredients__servings-label"
            htmlFor="servingsCount"
          >
            {lang.servingsLabel}
          </label>
          <div className="ingredients__servings-panel">
            <input
              className="ingredients__servings-count"
              id="servingsCount"
              type="number"
              min="1"
              value={isNaN(currentServings) ? '' : currentServings}
              onChange={e => handleServingsInputChange(e.target.value)}
              onKeyDown={handleKeyDown}
              onPaste={handlePaste}
            />
            <button
              className="ingredients__servings-button ingredients__servings-button_plus"
              tabIndex="0"
              onClick={increaseServings}
              aria-label={lang.increaseNumberOfServingsAriaLabel}
            >
              +
            </button>
            <button
              className="ingredients__servings-button ingredients__servings-button_minus"
              tabIndex="0"
              onClick={decreaseServings}
              aria-label={lang.decreaseNumberOfServingsAriaLabel}
            >
              -
            </button>
          </div>
        </div>

        {currentSections && (
          <form aria-live="polite" aria-atomic="true">
            {currentSections.map((section, sectionIndex) => (
              <fieldset
                className="ingredients__section"
                key={section + sectionIndex}
              >
                {section.heading && (
                  <legend className="ingredients__section-heading">
                    {section.heading}
                  </legend>
                )}
                {section.ingredients &&
                  section.ingredients.map((ingredient, ingredientIndex) => {
                    const showIngredient =
                      ingredient.printOrder &&
                      ingredient.amount !== null &&
                      ingredient.amount !== undefined;
                    return showIngredient ? (
                      <div
                        itemProp="ingredients"
                        key={'ingredient' + ingredientIndex}
                        className="ingredients__section-item"
                      >
                        <Ingredient {...ingredient} />
                      </div>
                    ) : null;
                  })}
              </fieldset>
            ))}
          </form>
        )}
      </section>
    </ExpandablePanel>
  );
};

Ingredients.propTypes = {
  heading: PropTypes.string,
  lang: PropTypes.shape({
    mobileShowLabel: PropTypes.string.isRequired,
    mobileHideLabel: PropTypes.string.isRequired,
    servingsLabel: PropTypes.string.isRequired,
    increaseNumberOfServingsAriaLabel: PropTypes.string.isRequired,
    decreaseNumberOfServingsAriaLabel: PropTypes.string.isRequired,
  }),
  servingsDefaultNumber: PropTypes.number,
  sections: PropTypes.arrayOf(
    PropTypes.shape({
      heading: PropTypes.string,
      ingredients: PropTypes.arrayOf(
        PropTypes.shape({
          printOrder: PropTypes.oneOf(['aui', 'iau', 'iua']), //Should be the same as print-order.js object
          amount: PropTypes.number.isRequired,
          unit: PropTypes.shape({
            plural: PropTypes.string,
            singular: PropTypes.string,
          }),
          name: PropTypes.shape({
            plural: PropTypes.string,
            singular: PropTypes.string,
          }),
        })
      ),
    })
  ),
};

Ingredients.viewModelMeta = {
  sections: [{ ingredients: [{ amount: 'double' }] }],
};

export default Ingredients;
